(() => {
  'use strict';

  if (window.HeroCoverflowPremium) return;

  const reduceMotion = () => window.matchMedia('(prefers-reduced-motion: reduce)').matches;

  const init = (root) => {
    if (!root || root.dataset.initialized === 'true') return;
    root.dataset.initialized = 'true';

    const cards = Array.from(root.querySelectorAll('[data-card]'));
    const backgrounds = Array.from(root.querySelectorAll('[data-background]'));
    const copies = Array.from(root.querySelectorAll('[data-copy]'));
    const dots = Array.from(root.querySelectorAll('[data-dot]'));
    const viewport = root.querySelector('[data-viewport]');
    const stage = root.querySelector('[data-stage]');
    const progress = root.querySelector('[data-progress]');
    const total = cards.length;

    if (!total) return;

    const config = {
      autoplay: root.dataset.autoplay === 'true' && total > 1 && !reduceMotion(),
      autoplayDelay: Number(root.dataset.autoplayDelay) || 5000,
      loop: root.dataset.loop === 'true',
      swipe: root.dataset.swipe === 'true',
      keyboard: root.dataset.keyboard === 'true',
      trackpad: root.dataset.trackpad === 'true',
      parallax: root.dataset.parallax === 'true',
      visibleCards: Math.max(2, Number(root.dataset.visibleCards) || 4)
    };

    let active = 0;
    let autoplayTimer;
    let wheelLocked = false;
    let isVisible = true;
    let isPaused = false;
    let pointer;
    let parallaxFrame = 0;
    let suppressClickUntil = 0;
    let observer;
    const cleanups = [];

    const on = (target, event, callback, options) => {
      target.addEventListener(event, callback, options);
      cleanups.push(() => target.removeEventListener(event, callback, options));
    };

    const normaliseIndex = (index) => {
      if (config.loop) return (index % total + total) % total;
      return Math.max(0, Math.min(index, total - 1));
    };

    const getOffset = (index) => {
      let offset = index - active;
      if (config.loop && total > 2) {
        if (offset > total / 2) offset -= total;
        if (offset < -total / 2) offset += total;
      }
      return offset;
    };

    const updateCardPositions = () => {
      const visibleLimit = Math.ceil(config.visibleCards / 2) + 1;
      const styles = window.getComputedStyle(root);
      const cardWidth = cards[active].getBoundingClientRect().width;
      const cardGap = Number.parseFloat(styles.getPropertyValue('--hcf-card-gap')) || 0;
      const cardDepth = Math.min(Number.parseFloat(styles.getPropertyValue('--hcf-coverflow-depth')) || 18, 30);
      const activeScale = Number.parseFloat(styles.getPropertyValue('--hcf-active-scale')) || 1;
      const inactiveOpacity = Number.parseFloat(styles.getPropertyValue('--hcf-inactive-opacity')) || .65;

      cards.forEach((card, index) => {
        const offset = getOffset(index);
        const distance = Math.abs(offset);
        const x = offset * (cardWidth + cardGap);
        const y = distance * cardDepth;
        const scale = offset === 0 ? activeScale : Math.max(.72, .93 - distance * .075);
        const opacity = offset === 0 ? 1 : Math.max(.12, inactiveOpacity - (distance - 1) * .16);

        card.style.setProperty('--hcf-card-x', `${x}px`);
        card.style.setProperty('--hcf-card-y', `${y}px`);
        card.style.setProperty('--hcf-card-scale', scale);
        card.style.setProperty('--hcf-card-opacity', opacity);
        card.style.zIndex = String(20 - distance);
        card.classList.toggle('is-active', offset === 0);
        card.classList.toggle('is-out-of-range', distance > visibleLimit);
        card.setAttribute('aria-current', offset === 0 ? 'true' : 'false');
        card.tabIndex = distance <= visibleLimit ? 0 : -1;
      });
    };

    const stopAutoplay = () => {
      window.clearTimeout(autoplayTimer);
      autoplayTimer = undefined;
      if (progress) {
        progress.classList.remove('is-running');
        progress.style.width = '0';
      }
    };

    const updateProgress = () => {
      if (!progress || !config.autoplay || isPaused || !isVisible) return;
      progress.classList.remove('is-running');
      progress.style.width = '0';
      window.requestAnimationFrame(() => {
        progress.style.removeProperty('width');
        progress.classList.add('is-running');
      });
    };

    const startAutoplay = () => {
      stopAutoplay();
      if (!config.autoplay || isPaused || !isVisible) return;
      updateProgress();
      autoplayTimer = window.setTimeout(() => goTo(active + 1), config.autoplayDelay);
    };

    const goTo = (index, userInitiated = false) => {
      const target = normaliseIndex(index);

      if (!config.loop && target === active && index !== active) {
        stopAutoplay();
        return;
      }

      if (target === active && index === active) {
        if (userInitiated) startAutoplay();
        return;
      }

      active = target;

      backgrounds.forEach((background, itemIndex) => {
        background.classList.toggle('is-active', itemIndex === active);
      });

      copies.forEach((copy, itemIndex) => {
        const selected = itemIndex === active;
        copy.classList.toggle('is-active', selected);
        copy.setAttribute('aria-hidden', selected ? 'false' : 'true');
        if ('inert' in copy) copy.inert = !selected;
      });

      dots.forEach((dot, itemIndex) => {
        const selected = itemIndex === active;
        dot.classList.toggle('is-active', selected);
        dot.setAttribute('aria-selected', selected ? 'true' : 'false');
      });

      updateCardPositions();
      startAutoplay();
    };

    cards.forEach((card, index) => {
      on(card, 'click', () => {
        if (Date.now() < suppressClickUntil) return;
        const destination = card.dataset.cardLink;

        if (index === active && destination) {
          if (card.dataset.cardLinkNewTab === 'true') window.open(destination, '_blank', 'noopener');
          else window.location.assign(destination);
          return;
        }

        goTo(index, true);
      });
    });

    dots.forEach((dot) => on(dot, 'click', () => goTo(Number(dot.dataset.index), true)));
    const previous = root.querySelector('[data-prev]');
    const next = root.querySelector('[data-next]');
    if (previous) on(previous, 'click', () => goTo(active - 1, true));
    if (next) on(next, 'click', () => goTo(active + 1, true));



    if (config.keyboard) {
      on(document, 'keydown', (event) => {
        const tag = event.target.tagName;
        const isEditing = ['INPUT', 'TEXTAREA', 'SELECT'].includes(tag) || event.target.isContentEditable;

        if (isEditing) return;

        if (event.key === 'ArrowLeft') {
          event.preventDefault();
          goTo(active - 1, true);
        } else if (event.key === 'ArrowRight') {
          event.preventDefault();
          goTo(active + 1, true);
        }
      });
    }



    if (viewport && config.swipe) {
      on(viewport, 'pointerdown', (event) => {
        if (event.pointerType === 'mouse' && event.button !== 0) return;
        pointer = { id: event.pointerId, startX: event.clientX, lastX: event.clientX };
        viewport.setPointerCapture?.(event.pointerId);
        viewport.classList.add('is-dragging');
        stopAutoplay();
      });

      on(viewport, 'pointermove', (event) => {
        if (!pointer || pointer.id !== event.pointerId) return;
        pointer.lastX = event.clientX;
        const delta = Math.max(-100, Math.min(100, event.clientX - pointer.startX));
        stage?.style.setProperty('--hcf-drag-x', `${delta}px`);
      });

      const endPointer = (event) => {
        if (!pointer || pointer.id !== event.pointerId) return;
        const delta = pointer.lastX - pointer.startX;
        viewport.classList.remove('is-dragging');
        stage?.style.setProperty('--hcf-drag-x', '0px');
        viewport.releasePointerCapture?.(event.pointerId);
        pointer = undefined;

        if (Math.abs(delta) > 42) {
          suppressClickUntil = Date.now() + 250;
          goTo(active + (delta < 0 ? 1 : -1), true);
        } else {
          startAutoplay();
        }
      };

      on(viewport, 'pointerup', endPointer);
      on(viewport, 'pointercancel', endPointer);
    }

    if (viewport && config.trackpad) {
      on(viewport, 'wheel', (event) => {
        const delta = Math.abs(event.deltaX) > Math.abs(event.deltaY)
          ? event.deltaX
          : (event.shiftKey ? event.deltaY : 0);
        if (!delta || wheelLocked) return;
        event.preventDefault();
        wheelLocked = true;
        goTo(active + (delta > 0 ? 1 : -1), true);
        window.setTimeout(() => { wheelLocked = false; }, 520);
      }, { passive: false });
    }

    on(root, 'mouseenter', () => {
      isPaused = true;
      stopAutoplay();
    });

    on(root, 'mouseleave', () => {
      isPaused = false;
      startAutoplay();
    });

    on(document, 'visibilitychange', () => {
      isVisible = !document.hidden && root.classList.contains('is-in-view');
      if (isVisible) startAutoplay();
      else stopAutoplay();
    });

    if (config.parallax && !reduceMotion()) {
      on(root, 'pointermove', (event) => {
        if (event.pointerType === 'touch') return;
        const rect = root.getBoundingClientRect();
        const x = ((event.clientX - rect.left) / rect.width - .5) * -Number(root.dataset.parallaxStrength || 10);
        const y = ((event.clientY - rect.top) / rect.height - .5) * -Number(root.dataset.parallaxStrength || 10);
        window.cancelAnimationFrame(parallaxFrame);
        parallaxFrame = window.requestAnimationFrame(() => {
          root.style.setProperty('--hcf-parallax-x', `${x}px`);
          root.style.setProperty('--hcf-parallax-y', `${y}px`);
        });
      });

      on(root, 'pointerleave', () => {
        root.style.setProperty('--hcf-parallax-x', '0px');
        root.style.setProperty('--hcf-parallax-y', '0px');
      });
    }

    if ('IntersectionObserver' in window) {
      observer = new IntersectionObserver((entries) => {
        entries.forEach((entry) => {
          root.classList.toggle('is-in-view', entry.isIntersecting);
          isVisible = entry.isIntersecting && !document.hidden;
          if (isVisible) startAutoplay();
          else stopAutoplay();
        });
      }, { threshold: .16 });
      observer.observe(root);
    } else {
      root.classList.add('is-in-view');
    }

    on(root, 'shopify:block:select', (event) => {
      const index = cards.findIndex((card) => card.dataset.blockId === event.detail.blockId);
      if (index > -1) goTo(index, true);
    });

    root.hcfDestroy = () => {
      stopAutoplay();
      observer?.disconnect();
      window.cancelAnimationFrame(parallaxFrame);
      cleanups.forEach((cleanup) => cleanup());
      delete root.dataset.initialized;
    };

    updateCardPositions();
    startAutoplay();
  };

  const initAll = (scope = document) => {
    if (scope.matches?.('[data-hero-coverflow]')) init(scope);
    scope.querySelectorAll?.('[data-hero-coverflow]').forEach(init);
  };

  const destroyAll = (scope) => {
    if (scope.matches?.('[data-hero-coverflow]')) scope.hcfDestroy?.();
    scope.querySelectorAll?.('[data-hero-coverflow]').forEach((root) => root.hcfDestroy?.());
  };

  window.HeroCoverflowPremium = { init, initAll, destroyAll };

  if (document.readyState === 'loading') document.addEventListener('DOMContentLoaded', () => initAll());
  else initAll();

  document.addEventListener('shopify:section:load', (event) => initAll(event.target));
  document.addEventListener('shopify:section:unload', (event) => destroyAll(event.target));
})();
